import { Component, Input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { CompanyWithSlotPeriodIdsResponse, EventInfoWithConsentResponse, InvestorMeetingCreateRequest, SlotPeriod, StockExchangeResponse } from '@ent-regis/entregis-ts-angular';
import { AttendancePreferenceEnum } from 'src/app/register-investor/models/register-investor.model';
import { AlertService } from 'src/app/shared/services/alert.service';

interface SlotDateGroup {
  date: Date;
  ranges: string[];
  virtualRanges: string[];
}

@Component({
  selector: 'app-investor-company-select-section',
  templateUrl: './investor-company-select-section.component.html',
  styleUrls: ['./investor-company-select-section.component.scss']
})
export class InvestorCompanySelectSectionComponent {

  @Input() companySelectForm!: FormGroup;
  @Input() isClosedRegis: boolean = false;
  @Input() companies: CompanyWithSlotPeriodIdsResponse[] = [];
  @Input() defaultMeetingType: InvestorMeetingCreateRequest.MeetingTypeEnum = InvestorMeetingCreateRequest.MeetingTypeEnum.GROUP;
  @Input() defaultMeetingPriority: InvestorMeetingCreateRequest.MeetingPriorityEnum = InvestorMeetingCreateRequest.MeetingPriorityEnum.MEDIUM;
  @Input() attendancePreference!: AttendancePreferenceEnum;
  @Input() eventInfoWithConsent!: EventInfoWithConsentResponse;
  @Input() stockExchanges: StockExchangeResponse[] = [];
  
  MeetingTypeEnum = InvestorMeetingCreateRequest.MeetingTypeEnum;
  MeetingPriorityEnum = InvestorMeetingCreateRequest.MeetingPriorityEnum;
  AttendancePreferenceEnum = AttendancePreferenceEnum;
  eventTypeEnum = EventInfoWithConsentResponse.EventTypeEnum;

  get companiesFA(): FormArray {
    return this.companySelectForm.get('companies') as FormArray;
  }

  get timezone(): string | null {
    return this.eventInfoWithConsent && this.eventInfoWithConsent.eventType === this.eventTypeEnum.THAILANDFOCUS ? 'UTC+7' : null;
  }

  constructor(
    private alert: AlertService,
    private datePipe: DatePipe,
  ) { }

  isSelected(company: CompanyWithSlotPeriodIdsResponse): boolean {
    return this.companiesFA.controls.some(
      ctrl => ctrl.get('registerCompanyId').value === company.registerCompanyId
    );
  }

  onSelectCompany(company: CompanyWithSlotPeriodIdsResponse): void {
    if (this.isClosedRegis) {
      return;
    }

    const idx = this.companiesFA.controls.findIndex(
      ctrl => ctrl.get('registerCompanyId').value === company.registerCompanyId
    );
    if (idx >= 0) {
      this.companiesFA.removeAt(idx);
    } else {
      this.companiesFA.push(new FormGroup({
        registerCompanyId: new FormControl(company.registerCompanyId),
        meetingType: new FormControl(this.defaultMeetingType),
        meetingPriority: new FormControl(this.defaultMeetingPriority),
        slotPeriodIds: new FormControl(company.slotPeriodIds || []),
      }));
    }
  }

  getSelectedCompanyForm(company: CompanyWithSlotPeriodIdsResponse): FormGroup | null {
    const selectedControl = this.companiesFA.controls.find(
      ctrl => ctrl.get('registerCompanyId').value === company.registerCompanyId
    );

    return selectedControl ? (selectedControl as FormGroup) : null;
  }

  getSlotDateGroups(company: CompanyWithSlotPeriodIdsResponse): SlotDateGroup[] {
    const slotPeriods = company.slotPeriods || [];
    const groups = new Map<string, SlotDateGroup>();

    slotPeriods.forEach((slot: SlotPeriod) => {
      if (!slot.slotDate) {
        return;
      }

      const date = new Date(slot.slotDate);
      const key = this.getDateKey(date);

      if (!groups.has(key)) {
        groups.set(key, { date, ranges: [], virtualRanges: [] });
      }

      if (!slot.slotStartTime || !slot.slotEndTime) {
        return;
      }

      const start = new Date(slot.slotStartTime);
      const end = new Date(slot.slotEndTime);
      const range = `${this.formatTime(start)}-${this.formatTime(end)}`;

      if (slot.slotType === SlotPeriod.SlotTypeEnum.VIRTUAL) {
        groups.get(key).virtualRanges.push(range);
      } else {
        groups.get(key).ranges.push(range);
      }
    });

    return Array.from(groups.values())
      .map(group => ({
        date: group.date,
        ranges: this.uniqueSortedRanges(group.ranges),
        virtualRanges: this.uniqueSortedRanges(group.virtualRanges),
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  formatTime(time: Date): string {
    return this.datePipe.transform(time, 'HH:mm', this.timezone);
  }

  private uniqueSortedRanges(ranges: string[]): string[] {
    const uniq = Array.from(new Set(ranges));

    return uniq.sort((a, b) => this.extractStartMinute(a) - this.extractStartMinute(b));
  }

  private getDateKey(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd', this.timezone);
  }

  private extractStartMinute(range: string): number {
    const start = range.split('-')[0] || '00:00';
    const [hourText, minuteText] = start.split(':');
    const hour = Number(hourText);
    const minute = Number(minuteText);
    return (hour * 60) + minute;
  }

  getExchangeName(stockExchangeId: number): string | null {
    const exchange = this.stockExchanges.find(e => e.id === stockExchangeId);
    return exchange ? exchange.name : null;
  }

  openCompanyDescription(company: CompanyWithSlotPeriodIdsResponse): void {
    if (!company) return;

    const name = company.companyName || '-';
    const symbol = company.companySymbol;
    const description = company.businessType || '-';

    if (symbol) {
      this.alert.showInfo(`(${symbol}) ${name}`, description);
      return;
    }

    this.alert.showInfo(name, description);
  }

}
